/* eslint-disable-next-line */
import { useUpdateAiWorkersMutation, useEntityResourcePermissions } from '@circuitry-ai/doc-data';
import { checkType, CtyButton, CtyShowToast } from '@circuitry-ai/doc-ui';
import { Icon } from '@iconify/react';
import { Divider, Input, Link, Select, SelectItem, Tooltip } from '@nextui-org/react';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

export interface AnalystweightProps { }

export function Analystweight(props: AnalystweightProps) {

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const aiworkerId = searchParams.get('aiworkerId');
  const { canUpdate } = useEntityResourcePermissions('analyst', aiworkerId ?? undefined);
  const { report } = location?.state ?? {};

  const [totalSum, setTotalSum] = useState<number | null>(null);
  const [updateAiWorkers, { isLoading: isUpdataingAiworkerData }] = useUpdateAiWorkersMutation();
  const [userOptionList, setUserOptionList] = useState<any[]>([]);

  const weightOptions = [
    { label: 'Clarity Checker', value: 'clarity_checker'}, 
    { label: 'Validation Checker', value:  'validation_checker'},
    { label: 'Loss Code Prediction', value: 'loss_code_prediction'},
    { label: 'Repair Profile', value: 'repair_profile'}
  ];

  useEffect(() => {
    if (report?.aiworkers_attrs?.length > 0) {
      handleReset();
    }
  }, [report]);

  const handleReset = () => {
    const weightsMap = report?.aiworkers_attrs?.find((attr: any) => attr.attr_name === 'weights')?.attr_json;
    if (weightsMap) {
      const options = Object.entries(weightsMap).map(([key, value]) => ({
        label: key,
        value: value
      }));
      setUserOptionList(options);
    } else {
      setUserOptionList([]);
    }
  };

  const addNewRow = () => {
    setUserOptionList((prev) => [...prev, { label: 'claims_dollars_per_vin', value: 0 }]);
  };

  const deleteOption = (index: number) => {
    setUserOptionList((prev) => {
      const updatedList = [...prev];
      updatedList.splice(index, 1);
      return updatedList;
    });
  };

  const handleSaveAnalystWeight = async () => {

    try {
      const labels = userOptionList.map(item => item.label);
      const uniqueLabels = new Set(labels);
      if (uniqueLabels.size !== labels.length) {
        CtyShowToast(checkType.error, 'Duplicate criteria selected. Please ensure each criteria is unique.');
        return;
      }

      const sum = userOptionList.map(item => Number(item.value)).reduce((acc, val) => acc + val, 0);
      if (sum !== 100) {
        setTotalSum(sum);
        CtyShowToast(checkType.error, `Total weights must sum to 100 (currently ${sum}).`);
        return;
      }
      setTotalSum(100);

      const payLoad = {
        aiworkers_attrs: [
          {
            attr_name: 'weights',
            attr_value: null,
            attr_json: userOptionList.reduce((obj, { label, value }) => {
              obj[label] = Number(value);
              return obj;
            }, {} as Record<string, number>)
          }
        ]
      };

      const response = await updateAiWorkers({ payLoad, aiworker_id: aiworkerId });
      navigate(`${location.pathname}${location.search}`, { state: { report: response?.data?.data } });
      CtyShowToast(checkType.success, response?.data?.message);

    } catch (error) {
      CtyShowToast(checkType.error, (error as Error)?.message);
    }
  };

  const footerItems = () => {
    return (
      <div className={`flex flex-row justify-between items-center gap-2`}>
        <div className='font-poppins font-normal text-[12px] leading-[16px] tracking-[0.15px] text-center align-middle'>Total score of all criteria added together must = 100</div>
        <div className='flex flex-row gap-2'>
          <CtyButton variant="bordered" color="primary" size="md" radius="sm" onPress={addNewRow} isDisabled={!canUpdate}>
            Add Row
          </CtyButton>
          <CtyButton variant="solid" color="primary" size="md" radius="sm" onPress={handleSaveAnalystWeight} isLoading={isUpdataingAiworkerData} isDisabled={!canUpdate}>
            Submit
          </CtyButton>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full gap-3">
      {totalSum && totalSum !== 100 && <div className="bg-[#f5e7b8] border-l-4 border-danger text-danger p-4 mb-2" role="alert">
        <div className="flex flex-row items-center gap-1">
          <Icon icon="ph:shield-warning" width="30" height="25" />
          <p className="font-medium">Total weights sum should be exactly 100, current total = {`${totalSum}`}</p>
        </div>
      </div>}
      {footerItems()}
      {userOptionList?.length > 0 && userOptionList?.map((item: string, index: number) => (
        <div key={index + '-' + item} className={`flex flex-row gap-3 items-center`}>
          <Select
            size="sm"
            className="max-w-xs"
            placeholder="Select an Advisor"
            aria-label="advisor-selection"
            selectedKeys={new Set([userOptionList[index]?.label])}
            disallowEmptySelection={true}
            onSelectionChange={(keys) => {
              const advisor_id = [...keys][0];
              setUserOptionList((prev) => {
                const updatedList = [...prev];
                updatedList[index] = { ...updatedList[index], label: advisor_id };
                return updatedList;
              });
            }}
          >
            {weightOptions?.map((advisor: { label: string, value: string; }) => (
              <SelectItem key={advisor.value} value={advisor.value}>
                {advisor.label}
              </SelectItem>
            ))}
          </Select>
          <Input
            id={item + '-' + index}
            size="sm"
            type={'number'}
            className={`w-[70px]`}
            value={userOptionList[index]?.value}
            onValueChange={(value) => setUserOptionList((prev) => {
              const updatedList = [...prev];
              updatedList[index] = { ...updatedList[index], value: value };
              return updatedList;
            })}
          />
          <Tooltip size="sm" color="danger" content={"Delete"}>
            <span className="relative text-danger cursor-pointer active:opacity-50">
              <Icon icon="hugeicons:delete-02" width="20" height="18" onClick={() => deleteOption(index)} />
            </span>
          </Tooltip>
        </div>
      ))}
      <Divider />
      <Link onPress={handleReset} className='cursor-pointer' isDisabled={!canUpdate}><span className="font-poppins font-semibold text-[14px] leading-[20px] tracking-[0px] text-primary mb-3">Reset to default</span></Link>
    </div>
  );
}

export default Analystweight;
